import { ColumnDef } from '@tanstack/react-table';
import { Stack } from 'react-bootstrap';
import { BsGeoAlt } from 'react-icons/bs';
import CheckmarkCell from '../../../components/tables/cellComponents/CheckmarkCell';
import { DateCell } from '../../../components/tables/cellComponents/DateCell';
import { EnrollmentCell } from '../../../components/tables/cellComponents/EnrollmentCell';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { VenueCell } from '../../../components/tables/cellComponents/VenueCell';
import { Lesson, LessonAssignment } from '../../../types/lesson.type';
import {displaySessionTimeRange, displaySessionTimeRangeFromFormatted, isMissingSessionTime} from '../../dateUtils';
import {
  getLessonInstructorDisplay,
  lessonInstructorDisplaySwatchClass
} from '../../lessonInstructorUtils';
const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const classColumns: ColumnDef<Lesson>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Date',
    accessorKey: 'date',
    cell: ({ row }) => (
      <>
        <DateCell date={row.original.date} />
        <p>{row.original.date_day_name ?? weekday[new Date(row.original.date).getDay()]}</p>
      </>
    )
  },
  {
    header: 'Time',
    accessorKey: 'formatted_time',
    cell: ({ row }) => {
      const timeDisplay = displaySessionTimeRange(
          isMissingSessionTime(row.original.calculated_starts_at)
              ? row.original.course?.starts_at
              : row.original.calculated_starts_at,
          isMissingSessionTime(row.original.calculated_ends_at)
              ? row.original.course?.ends_at
              : row.original.calculated_ends_at,
          (row.original.course?.venue as any)?.location?.timezone,
      );
      return <span className="text-nowrap">{timeDisplay || row.original.formatted_time}</span>;
    }
  },
  {
    header: 'Session',
    accessorKey: 'course.title',
    enableSorting: false,
    cell: ({ row }) => (
      <LinkCell
        content={row.original.course.title}
        url={`/admin/schedule/sessions/${row.original.course.id}`}
      />
    )
  },
  {
    header: 'Location',
    accessorKey: 'course.venue.title',
    enableSorting: false,
    cell: ({ row }) => <VenueCell venue={row.original.course.venue} />
  },
  {
    header: 'First Class ?',
    accessorKey: 'first_lesson',
    enableSorting: false,
    cell: ({ getValue }) => <CheckmarkCell value={getValue<number>() === 1 ? true : false} />
  },
  {
    header: 'Last Class ?',
    accessorKey: 'last_lesson',
    enableSorting: false,
    cell: ({ getValue }) => <CheckmarkCell value={getValue<number>() === 1 ? true : false} />
  },
  {
    header: 'Need Sub ?',
    accessorKey: 'needs_sub',
    enableSorting: false,
    cell: ({ getValue }) => <CheckmarkCell value={Number(getValue()) === 1} />
  },
  {
    header: 'Enrollment',
    accessorKey: 'enrollment',
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <>
        <Stack
          className="align-items-center justify-content-center"
          direction="vertical"
          gap={2}
        >
          <span className="text-nowrap text-center">{row.original.course.enrollment}</span>
          <div className="d-flex justify-content-center">
            <EnrollmentCell
              value={getValue<string>()}
              type={row.original.course.payment_type}
            />
          </div>
        </Stack>
      </>
    )
  },
  {
    header: 'Instructor',
    id: 'lesson_instructor',
    accessorKey: 'lesson_assignments',
    enableSorting: false,
    accessorFn: (data) => getLessonInstructorDisplay(data, data.course).map((i) => i.name),
    cell: ({ row }) => {
      const instructors = getLessonInstructorDisplay(row.original, row.original.course);
      return (
        <>
          {instructors.map((instructor) => (
            <div
              key={`${instructor.id}-${instructor.role}`}
              className={`swatch ${lessonInstructorDisplaySwatchClass(instructor, row.original.lesson_assignments)}`}
            >
              {instructor.name}
            </div>
          ))}
        </>
      );
    }
  }
];

export const needSubClassesColumns: ColumnDef<LessonAssignment>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Requesting Instructor',
    id: 'lesson_instructor',
    accessorKey: 'lesson.requesting_instructor.full_name',
    cell: ({ row }) => {
      const { id, full_name } = row.original.requesting_instructor;
      return (
        <LinkCell
          content={`${full_name}`}
          url={`/admin/dash-users/instructors/${id}`}
        />
      );
    }
  },
  {
    header: 'Session',
    accessorKey: 'lesson.course.title',
    cell: ({ row, getValue }) => (
      <LinkCell
        content={getValue<string>()}
        url={`/admin/schedule/sessions/${row.original.course_id}`}
      />
    )
  },
  {
    header: 'Venue',
    accessorKey: 'lesson.course.venue.title',
    cell: ({ row }) => <VenueCell venue={row.original.lesson.course.venue} />
  },
  {
    header: 'Map',
    accessorKey: 'lesson.course.venue.title',
    cell: ({ row }) => (
      <BsGeoAlt
        size={16}
        className="text-info mt-1"
        onClick={() =>
          window.open(
            `https://maps.google.com/?q=${row.original.lesson.course.venue.address}`,
            '_blank'
          )
        }
      />
    )
  },
  {
    header: 'Date',
    accessorKey: 'lesson.date',
    cell: ({ row }) => (
      <DateCell date={row.original.lesson.date} />
    )
  },
  {
    header: 'Day Of Week',
    accessorKey: 'lesson.course.day_of_week'
  },
  {
    header: 'Time',
    id: 'lesson.formatted_time',
    cell: ({ row }) => {
      const timeDisplay = displaySessionTimeRangeFromFormatted(
          row.original.lesson.formatted_time,
          (row.original.lesson.course?.venue as any)?.location?.timezone
      );
      return (
          <p className="text-nowrap">
            {timeDisplay || row.original.lesson.formatted_time}
          </p>
      );
    }
  }
];
